'use client';
/*
 * Documentation:
 * Badge — https://app.subframe.com/6fc7f5fbb098/library?component=Badge_97bdb082-1124-4dd7-a335-b14b822d0157
 */

import React from 'react';
import * as SubframeCore from '@subframe/core';
import * as SubframeUtils from '../utils';

interface BadgeRootProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'brand' | 'neutral' | 'error' | 'warning' | 'success' | 'info';
  icon?: React.ReactNode;
  children?: React.ReactNode;
  iconRight?: React.ReactNode;
  className?: string;
}

const BadgeRoot = React.forwardRef<HTMLDivElement, BadgeRootProps>(function BadgeRoot(
  {
    variant = 'brand',
    icon = null,
    children,
    iconRight = null,
    className,
    ...otherProps
  }: BadgeRootProps,
  ref,
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        'group/97bdb082 border-brand-100 bg-brand-100 flex h-6 w-fit items-center gap-1 rounded-md border border-solid px-2',
        {
          'border-success-100 bg-success-100 border border-solid': variant === 'success',
          'border-warning-100 bg-warning-100 border border-solid': variant === 'warning',
          'border-error-100 bg-error-100 border border-solid': variant === 'error',
          'border-information-100 bg-information-100 border border-solid': variant === 'info',
          'border border-solid border-neutral-100 bg-neutral-100': variant === 'neutral',
        },
        className,
      )}
      ref={ref}
      {...otherProps}
    >
      {icon ? (
        <SubframeCore.IconWrapper
          className={SubframeUtils.twClassNames('text-caption font-caption text-brand-700', {
            'text-success-800': variant === 'success',
            'text-warning-800': variant === 'warning',
            'text-error-700': variant === 'error',
            'text-information-800': variant === 'info',
            'text-neutral-700': variant === 'neutral',
          })}
        >
          {icon}
        </SubframeCore.IconWrapper>
      ) : null}
      {children ? (
        <span
          className={SubframeUtils.twClassNames(
            'text-caption font-caption text-brand-600 whitespace-nowrap',
            {
              'text-success-800': variant === 'success',
              'text-warning-800': variant === 'warning',
              'text-error-800': variant === 'error',
              'text-information-800': variant === 'info',
              'text-neutral-700': variant === 'neutral',
            },
          )}
        >
          {children}
        </span>
      ) : null}
      {iconRight ? (
        <SubframeCore.IconWrapper
          className={SubframeUtils.twClassNames('text-caption font-caption text-brand-700', {
            'text-success-800': variant === 'success',
            'text-warning-800': variant === 'warning',
            'text-error-700': variant === 'error',
            'text-information-800': variant === 'info',
            'text-neutral-700': variant === 'neutral',
          })}
        >
          {iconRight}
        </SubframeCore.IconWrapper>
      ) : null}
    </div>
  );
});

export const Badge = BadgeRoot;
